import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { toast } from "sonner";
import { 
  Search, 
  Filter,
  ExternalLink,
  Check,
  X,
  Flag,
  Bookmark,
  Edit3,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  Calendar,
  Tag
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Importance Badge with color coding
const ImportanceBadge = ({ importance }) => {
  if (!importance) return null;
  
  const colors = {
    high: "bg-[#ef4444]/20 text-[#ef4444] border-[#ef4444]/30",
    medium: "bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30",
    low: "bg-[#7a7a8e]/20 text-[#7a7a8e] border-[#7a7a8e]/30",
  };
  
  return (
    <Badge className={`font-mono text-[10px] uppercase rounded px-2 py-0.5 ${colors[importance] || colors.low}`}>
      {importance}
    </Badge>
  );
};

// Category Badge
const CategoryBadge = ({ category, taxonomyMap }) => {
  if (!category) return null;
  
  const option = taxonomyMap?.category?.find(o => o.value === category);
  const label = option?.label || category.replace(/_/g, ' ');
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge className="font-mono text-[10px] bg-[#60a5fa]/20 text-[#60a5fa] border-[#60a5fa]/30 rounded px-2 py-0.5 uppercase">
            {label}
          </Badge>
        </TooltipTrigger>
        {option?.description && (
          <TooltipContent className="bg-[#12121a] border-[#2a2a3a] max-w-xs">
            <p className="text-xs">{option.description}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

// Signal Icon
const SignalIcon = ({ signal }) => {
  if (!signal) return null;
  
  const icons = {
    bullish: <TrendingUp className="w-4 h-4 text-[#6ee7b7]" />,
    bearish: <TrendingDown className="w-4 h-4 text-[#ef4444]" />,
    neutral: <Minus className="w-4 h-4 text-[#7a7a8e]" />,
    mixed: <Minus className="w-4 h-4 text-[#f59e0b]" />,
  };
  
  return icons[signal] || null;
};

// Audience Badge
const AudienceBadge = ({ audience }) => {
  if (!audience) return null;
  
  const colors = {
    beginner: "bg-[#60a5fa]/20 text-[#60a5fa] border-[#60a5fa]/30",
    pro: "bg-[#a78bfa]/20 text-[#a78bfa] border-[#a78bfa]/30",
    both: "bg-[#6ee7b7]/20 text-[#6ee7b7] border-[#6ee7b7]/30",
  };
  
  return (
    <Badge className={`font-mono text-[10px] uppercase rounded px-2 py-0.5 ${colors[audience] || colors.both}`}>
      {audience}
    </Badge>
  );
};

// Urgency Badge
const UrgencyBadge = ({ urgency }) => {
  if (!urgency) return null;
  
  const colors = {
    breaking: "bg-[#ef4444]/20 text-[#ef4444] border-[#ef4444]/30",
    time_sensitive: "bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30",
    timely: "bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30",
    current: "bg-[#7a7a8e]/20 text-[#7a7a8e] border-[#7a7a8e]/30",
    developing: "bg-[#60a5fa]/20 text-[#60a5fa] border-[#60a5fa]/30",
    evergreen: "bg-[#7a7a8e]/20 text-[#7a7a8e] border-[#7a7a8e]/30",
  };
  
  return (
    <Badge className={`font-mono text-[10px] uppercase rounded px-2 py-0.5 ${colors[urgency] || colors.current}`}>
      {urgency.replace('_', '-')}
    </Badge>
  );
};

// Status Badge
const StatusBadge = ({ status }) => {
  const colors = {
    pending: "bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30",
    processed: "bg-[#60a5fa]/20 text-[#60a5fa] border-[#60a5fa]/30",
    completed: "bg-[#6ee7b7]/20 text-[#6ee7b7] border-[#6ee7b7]/30",
    in_progress: "bg-[#f59e0b]/20 text-[#f59e0b] border-[#f59e0b]/30",
    planned: "bg-[#a78bfa]/20 text-[#a78bfa] border-[#a78bfa]/30",
    approved: "bg-[#6ee7b7]/20 text-[#6ee7b7] border-[#6ee7b7]/30",
    rejected: "bg-[#ef4444]/20 text-[#ef4444] border-[#ef4444]/30",
    flagged: "bg-[#a78bfa]/20 text-[#a78bfa] border-[#a78bfa]/30",
  };
  
  return (
    <Badge className={`font-mono text-[10px] uppercase rounded px-2 py-0.5 ${colors[status] || colors.pending}`}>
      {status?.replace('_', '-')}
    </Badge>
  );
};

// Assets Pills
const AssetsPills = ({ assets }) => {
  if (!assets) return null;
  
  const assetList = assets.split(',').map(a => a.trim()).filter(Boolean);
  if (assetList.length === 0) return null;
  
  return (
    <div className="flex gap-1 flex-wrap">
      {assetList.map((asset, idx) => (
        <span 
          key={idx} 
          className="px-1.5 py-0.5 bg-[#6ee7b7]/10 text-[#6ee7b7] border border-[#6ee7b7]/30 rounded text-[10px] font-mono"
        >
          {asset}
        </span>
      ))}
    </div>
  );
};

// Edit Dialog
const EditDialog = ({ item, open, onClose, onSave, taxonomyMap }) => {
  const [formData, setFormData] = useState({
    summary: "",
    editor_notes: "",
    category: "",
    importance: "",
    status: "",
    urgency: "",
    signal: "",
    audience: "",
    assets: "",
    event_date: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setFormData({
        summary: item.summary || "",
        editor_notes: item.editor_notes || "",
        category: item.category || "",
        importance: item.importance || "",
        status: item.status || "",
        urgency: item.urgency || "",
        signal: item.signal || "",
        audience: item.audience || "",
        assets: item.assets || "",
        event_date: item.event_date || "",
      });
    }
  }, [item]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== "" && value !== null) {
          updateData[key] = value;
        }
      });
      
      await api.patch(`/items/${item.id}`, updateData);
      toast.success("Item updated");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to update item");
    } finally {
      setSaving(false);
    }
  };

  const renderSelect = (field, label, options) => (
    <div>
      <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">{label}</Label>
      <Select value={formData[field] || "none"} onValueChange={(v) => setFormData({...formData, [field]: v === "none" ? "" : v})}>
        <SelectTrigger className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]">
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent className="bg-[#12121a] border-[#2a2a3a]">
          <SelectItem value="none">None</SelectItem>
          {options?.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#12121a] border-[#2a2a3a] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Item</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-3 bg-[#1a1a26] border border-[#2a2a3a] rounded text-sm text-[#7a7a8e]">
            {item?.raw_text}
          </div>
          
          <div>
            <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Summary</Label>
            <Textarea
              value={formData.summary}
              onChange={(e) => setFormData({...formData, summary: e.target.value})}
              className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a] min-h-[60px]"
              data-testid="edit-summary"
            />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {renderSelect('category', 'Category', taxonomyMap?.category)}
            {renderSelect('importance', 'Importance', taxonomyMap?.importance)}
            {renderSelect('status', 'Status', taxonomyMap?.status)}
            {renderSelect('urgency', 'Urgency', taxonomyMap?.urgency)}
            {renderSelect('signal', 'Signal', taxonomyMap?.signal)}
            {renderSelect('audience', 'Audience', taxonomyMap?.audience)}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Assets (comma-separated)</Label>
              <Input
                value={formData.assets}
                onChange={(e) => setFormData({...formData, assets: e.target.value.toUpperCase()})}
                placeholder="BTC, ETH, USDC"
                className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]"
                data-testid="edit-assets"
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Event Date</Label>
              <Input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({...formData, event_date: e.target.value})}
                className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a]"
                data-testid="edit-event-date"
              />
            </div>
          </div>
          
          <div>
            <Label className="font-mono text-[10px] uppercase text-[#7a7a8e]">Editor Notes</Label>
            <Textarea
              value={formData.editor_notes}
              onChange={(e) => setFormData({...formData, editor_notes: e.target.value})}
              className="mt-1.5 bg-[#1a1a26] border-[#2a2a3a] min-h-[60px]"
              placeholder="Add notes..."
              data-testid="edit-notes"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#7a7a8e]">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#6ee7b7] text-[#0a0a0f] hover:bg-[#6ee7b7]/90" data-testid="edit-save">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Item Card
const ItemCard = ({ item, onAction, onEdit, taxonomyMap }) => {
  const [loading, setLoading] = useState(null);

  const handleAction = async (action, data) => {
    setLoading(action);
    try {
      await onAction(item.id, action, data);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="p-4 bg-[#12121a] border border-[#2a2a3a] rounded hover:border-[#3a3a4a] transition-colors" data-testid={`item-${item.id}`}>
      <div className="flex gap-4">
        {/* Importance + Confidence + Signal column */}
        <div className="flex flex-col items-center gap-1 min-w-[48px]">
          <ImportanceBadge importance={item.importance} />
          {(item.confidence ?? item.importance_score) != null && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className={`font-mono text-[11px] font-semibold ${
                    (item.confidence ?? item.importance_score) >= 8 ? 'text-[#6ee7b7]' :
                    (item.confidence ?? item.importance_score) >= 5 ? 'text-[#f59e0b]' :
                    'text-[#ef4444]'
                  }`}>
                    {item.confidence ?? item.importance_score}/10
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-[#12121a] border-[#2a2a3a] max-w-xs">
                  <p className="text-xs">{item.confidence_reason || 'AI confidence score'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <SignalIcon signal={item.signal} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Badges Row */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <CategoryBadge category={item.category} taxonomyMap={taxonomyMap} />
            <AudienceBadge audience={item.audience} />
            <UrgencyBadge urgency={item.urgency} />
            {item.event_date && (
              <span className="flex items-center gap-1 text-[10px] font-mono text-[#7a7a8e]">
                <Calendar className="w-3 h-3" />
                {item.event_date}
              </span>
            )}
          </div>
          
          {/* Summary */}
          <p className="text-[#e0e0e8] mb-2 leading-relaxed">
            {item.summary || item.raw_text}
          </p>
          
          {/* Assets */}
          {item.assets && (
            <div className="mb-2">
              <AssetsPills assets={item.assets} />
            </div>
          )}
          
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#7a7a8e] font-mono">
            {item.source_url && (
              <a 
                href={item.source_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#60a5fa] hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                {item.source_url.includes('x.com') ? 'View on X' : 'Source'}
              </a>
            )}
            {item.source_author && <span>@{item.source_author}</span>}
            {item.source_timestamp && (
              <span>{new Date(item.source_timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            )}
            {item.tags && (
              <div className="flex items-center gap-1">
                <Tag className="w-3 h-3" />
                {item.tags.split(',').slice(0, 3).map((tag, i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-[#1a1a26] rounded text-[10px]">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Status & Actions */}
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={item.status} />
          
          <div className="flex items-center gap-1 mt-auto">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleAction('approve')}
              disabled={loading === 'approve'}
              className="h-7 w-7 p-0 text-[#7a7a8e] hover:text-[#6ee7b7] hover:bg-[#6ee7b7]/10"
              title="Approve"
              data-testid={`approve-${item.id}`}
            >
              <Check className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleAction('reject')}
              disabled={loading === 'reject'}
              className="h-7 w-7 p-0 text-[#7a7a8e] hover:text-[#ef4444] hover:bg-[#ef4444]/10"
              title="Reject"
              data-testid={`reject-${item.id}`}
            >
              <X className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleAction('flag')}
              disabled={loading === 'flag'}
              className="h-7 w-7 p-0 text-[#7a7a8e] hover:text-[#f59e0b] hover:bg-[#f59e0b]/10"
              title="Flag for Review"
              data-testid={`flag-${item.id}`}
            >
              <Flag className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleAction('newsletter')}
              disabled={loading === 'newsletter'}
              className={`h-7 w-7 p-0 ${item.newsletter_used ? 'text-[#a78bfa]' : 'text-[#7a7a8e] hover:text-[#a78bfa]'} hover:bg-[#a78bfa]/10`}
              title="Mark for Newsletter"
              data-testid={`newsletter-${item.id}`}
            >
              <Bookmark className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEdit(item)}
              className="h-7 w-7 p-0 text-[#7a7a8e] hover:text-[#60a5fa] hover:bg-[#60a5fa]/10"
              title="Edit"
              data-testid={`edit-${item.id}`}
            >
              <Edit3 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BrowsePage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [taxonomyMap, setTaxonomyMap] = useState({});
  const [showFilters, setShowFilters] = useState(true);
  
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    importance: "all",
    status: "all",
    urgency: "all",
    signal: "all",
    audience: "all",
    assets: "",
    event_date_from: "",
    event_date_to: "",
    date_from: "",
    date_to: "",
  });

  const fetchTaxonomy = async () => {
    try {
      const response = await api.get('/filters/options');
      setTaxonomyMap(response.data.taxonomy || {});
    } catch (error) {
      console.error("Failed to fetch taxonomy");
    }
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.category && filters.category !== 'all') params.append("category", filters.category);
      if (filters.importance && filters.importance !== 'all') params.append("importance", filters.importance);
      if (filters.status && filters.status !== 'all') params.append("status", filters.status);
      if (filters.urgency && filters.urgency !== 'all') params.append("urgency", filters.urgency);
      if (filters.signal && filters.signal !== 'all') params.append("signal", filters.signal);
      if (filters.audience && filters.audience !== 'all') params.append("audience", filters.audience);
      if (filters.assets) params.append("assets", filters.assets);
      if (filters.event_date_from) params.append("event_date_from", filters.event_date_from);
      if (filters.event_date_to) params.append("event_date_to", filters.event_date_to);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      
      const response = await api.get(`/items?${params.toString()}`);
      setItems(response.data);
    } catch (error) {
      toast.error("Failed to load items");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTaxonomy();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleAction = async (id, action, data) => {
    try {
      let updateData = {};
      
      switch (action) {
        case 'approve':
          updateData = { status: 'approved', flagged_for_review: false };
          break;
        case 'reject':
          updateData = { status: 'rejected', flagged_for_review: false };
          break;
        case 'flag':
          updateData = { flagged_for_review: true, status: 'flagged' };
          break;
        case 'newsletter':
          const item = items.find(i => i.id === id);
          updateData = { newsletter_used: !item?.newsletter_used };
          break;
        default:
          return;
      }
      
      await api.patch(`/items/${id}`, updateData);
      toast.success(`Item ${action}ed`);
      fetchItems();
    } catch (error) {
      toast.error(`Failed to ${action} item`);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      category: "all",
      importance: "all",
      status: "all",
      urgency: "all",
      signal: "all",
      audience: "all",
      assets: "",
      event_date_from: "",
      event_date_to: "",
      date_from: "",
      date_to: "",
    });
  };

  // Helper to render taxonomy select
  const renderTaxonomySelect = (field, label, placeholder = "All") => {
    const options = taxonomyMap[field] || [];
    return (
      <div>
        <Label className="font-mono text-[10px] uppercase text-[#7a7a8e] mb-1.5 block">{label}</Label>
        <Select value={filters[field]} onValueChange={(v) => setFilters({...filters, [field]: v})}>
          <SelectTrigger className="bg-[#1a1a26] border-[#2a2a3a]" data-testid={`filter-${field}`}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="bg-[#12121a] border-[#2a2a3a] max-h-[300px]">
            <SelectItem value="all">{placeholder}</SelectItem>
            {options.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  };

  return (
    <div data-testid="browse-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">Browse Items</h1>
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm text-[#7a7a8e]">
            {items.length} results
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="border-[#2a2a3a] text-[#7a7a8e] hover:text-[#e0e0e8]"
            data-testid="toggle-filters"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Status Quick Filters */}
      <div className="flex items-center gap-2 mb-4" data-testid="status-tabs">
        {[
          { value: 'all', label: 'All', color: 'text-[#e0e0e8] bg-[#1a1a26] border-[#2a2a3a]' },
          { value: 'needs_review', label: 'Needs Review', color: 'text-[#a78bfa] bg-[#a78bfa]/10 border-[#a78bfa]/30' },
          { value: 'approved', label: 'Approved', color: 'text-[#6ee7b7] bg-[#6ee7b7]/10 border-[#6ee7b7]/30' },
          { value: 'rejected', label: 'Rejected', color: 'text-[#ef4444] bg-[#ef4444]/10 border-[#ef4444]/30' },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setFilters({ ...filters, status: tab.value })}
            className={`h-8 px-3 text-xs font-mono uppercase rounded border transition-colors ${
              filters.status === tab.value
                ? tab.color
                : 'text-[#7a7a8e] bg-transparent border-[#2a2a3a] hover:border-[#3a3a4a]'
            }`}
            data-testid={`tab-${tab.value}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="bg-[#12121a] border-[#2a2a3a] mb-6" data-testid="filters-panel">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {/* Search */}
              <div className="col-span-2">
                <Label className="font-mono text-[10px] uppercase text-[#7a7a8e] mb-1.5 block">Search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7a7a8e]" />
                  <Input
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    placeholder="Search summary and content..."
                    className="pl-9 bg-[#1a1a26] border-[#2a2a3a]"
                    data-testid="filter-search"
                  />
                </div>
              </div>

              {/* Category - Dynamic from taxonomy */}
              {renderTaxonomySelect('category', 'Category', 'All categories')}

              {/* Importance - Dynamic from taxonomy */}
              {renderTaxonomySelect('importance', 'Importance', 'All')}

              {/* Status - Dynamic from taxonomy */}
              {renderTaxonomySelect('status', 'Status', 'All')}

              {/* Urgency - Dynamic from taxonomy */}
              {renderTaxonomySelect('urgency', 'Urgency', 'All')}

              {/* Signal - Dynamic from taxonomy */}
              {renderTaxonomySelect('signal', 'Signal', 'All')}

              {/* Audience - Dynamic from taxonomy */}
              {renderTaxonomySelect('audience', 'Audience', 'All')}

              {/* Assets Search */}
              <div>
                <Label className="font-mono text-[10px] uppercase text-[#7a7a8e] mb-1.5 block">Assets</Label>
                <Input
                  value={filters.assets}
                  onChange={(e) => setFilters({...filters, assets: e.target.value.toUpperCase()})}
                  placeholder="BTC, ETH..."
                  className="bg-[#1a1a26] border-[#2a2a3a]"
                  data-testid="filter-assets"
                />
              </div>

              {/* Event Date From */}
              <div>
                <Label className="font-mono text-[10px] uppercase text-[#7a7a8e] mb-1.5 block">Event From</Label>
                <Input
                  type="date"
                  value={filters.event_date_from}
                  onChange={(e) => setFilters({...filters, event_date_from: e.target.value})}
                  className="bg-[#1a1a26] border-[#2a2a3a]"
                  data-testid="filter-event-date-from"
                />
              </div>

              {/* Event Date To */}
              <div>
                <Label className="font-mono text-[10px] uppercase text-[#7a7a8e] mb-1.5 block">Event To</Label>
                <Input
                  type="date"
                  value={filters.event_date_to}
                  onChange={(e) => setFilters({...filters, event_date_to: e.target.value})}
                  className="bg-[#1a1a26] border-[#2a2a3a]"
                  data-testid="filter-event-date-to"
                />
              </div>

              {/* Batch Date From */}
              <div>
                <Label className="font-mono text-[10px] uppercase text-[#7a7a8e] mb-1.5 block">Batch From</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                  className="bg-[#1a1a26] border-[#2a2a3a]"
                  data-testid="filter-date-from"
                />
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-[#7a7a8e] hover:text-[#e0e0e8]"
                data-testid="clear-filters"
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Items List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-[#7a7a8e]">Loading items...</div>
        </div>
      ) : items.length === 0 ? (
        <Card className="bg-[#12121a] border-[#2a2a3a]">
          <CardContent className="p-8 text-center">
            <Search className="w-8 h-8 text-[#7a7a8e] mx-auto mb-3" />
            <p className="text-[#7a7a8e]">No items found</p>
            <p className="text-[#7a7a8e]/60 text-sm mt-1">Try adjusting your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <ItemCard
              key={item.id}
              item={item}
              onAction={handleAction}
              onEdit={setEditItem}
              taxonomyMap={taxonomyMap}
            />
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      {editItem && (
        <EditDialog
          item={editItem}
          open={!!editItem}
          onClose={() => setEditItem(null)}
          onSave={fetchItems}
          taxonomyMap={taxonomyMap}
        />
      )}
    </div>
  );
};

export default BrowsePage;
